// frontend/src/App.jsx
// SignalIQ Financial Intelligence — Application Router
//
// Enterprise route alignment:
// - Restores all core module routes so Sidebar links do not fall back to Dashboard.
// - Keeps lazy loading for heavy pages.
// - Keeps tester restrictions for admin/billing/team/model governance/model lab/AI workforce.
// - Preserves feature gates for standard beta modules.
// - Adds route aliases for legacy paths used across Sidebar, Topbar, bookmarks, and older components.

import React, { lazy, Suspense, memo, useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { AppProviders } from "./context/AppProviders";
import { useAuth } from "./context/AuthContext";

import AppShell from "./components/Layout/AppShell";
import PageLoading from "./components/PageLoading.jsx";
import ProtectedRoute from "./components/routes/ProtectedRoute";
import FeatureRoute from "./components/routes/FeatureRoute";
import adminApi from "./api/admin";

// ─────────────────────────────────────────────────────────────
// Loading UI
// ─────────────────────────────────────────────────────────────

const PageFallback = memo(function PageFallback() {
  return (
    <PageLoading
      label="Preparing SignalIQ Intelligence…"
      sublabel="Loading the selected workspace module."
    />
  );
});

function lazyPage(factory) {
  const Component = lazy(factory);

  return function LazyWrapped(props) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Component {...props} />
      </Suspense>
    );
  };
}

// ─────────────────────────────────────────────────────────────
// Public pages
// ─────────────────────────────────────────────────────────────

import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// ─────────────────────────────────────────────────────────────
// Lazy enterprise pages
// ─────────────────────────────────────────────────────────────

const DashboardPage = lazyPage(() => import("./pages/DashboardPage"));
const LegalPages = lazyPage(() => import("./pages/LegalPages"));
const OnboardingPage = lazyPage(() => import("./pages/OnboardingPage"));
const PortfolioPage = lazyPage(() => import("./pages/PortfolioPage"));
const CompaniesPage = lazyPage(() => import("./pages/CompaniesPage"));

const StrengthProfilePage = lazyPage(() =>
  import("./pages/StrengthProfilePage")
);

const BSPSurvivalPage = lazyPage(() =>
  import("./pages/BSPSurvivalPage")
);

const FHIProfilePage = lazyPage(() =>
  import("./pages/FHIProfilePage")
);

const BenchmarksPage = lazyPage(() =>
  import("./pages/BenchmarksPage")
);

const CovenantBreachPage = lazyPage(() =>
  import("./pages/CovenantBreachPage")
);

const EarlyWarningsPage = lazyPage(() =>
  import("./pages/EarlyWarningsPage")
);

const VariancePage = lazyPage(() =>
  import("./pages/VariancePage")
);

const UnitEconomicsPage = lazyPage(() =>
  import("./pages/UnitEconomicsPage")
);

const DecompositionPage = lazyPage(() =>
  import("./pages/DecompositionPage")
);

const ScenarioAnalysisPage = lazyPage(() =>
  import("./pages/ScenarioAnalysisPage")
);

const SensitivityPage = lazyPage(() =>
  import("./pages/SensitivityPage")
);

const GoalSeekPage = lazyPage(() =>
  import("./pages/GoalSeekPage")
);

const ForecastingPage = lazyPage(() =>
  import("./pages/ForecastingPage")
);

const InvestmentAppraisalPage = lazyPage(() =>
  import("./pages/InvestmentAppraisalPage")
);

const FinancialStatementsVisualsPage = lazyPage(() =>
  import("./pages/FinancialStatementsVisualsPage")
);

const CostBenefitPage = lazyPage(() =>
  import("./pages/CostBenefitPage")
);

const MacroIntelligencePage = lazyPage(() =>
  import("./pages/MacroIntelligencePage")
);

const DailyBriefingPage = lazyPage(() =>
  import("./pages/DailyBriefingPage")
);

const GlobalRiskMonitorPage = lazyPage(() =>
  import("./pages/GlobalRiskMonitorPage")
);

const ESGPage = lazyPage(() =>
  import("./pages/ESGPage")
);

const AIAnalystPage = lazyPage(() =>
  import("./pages/AIAnalystPage")
);

const IngestionPage = lazyPage(() =>
  import("./pages/IngestionPage")
);

const IngestionJobsPage = lazyPage(() =>
  import("./pages/IngestionJobsPage")
);

const IngestionReviewPage = lazyPage(() =>
  import("./pages/IngestionReviewPage")
);

const IngestionValidationPage = lazyPage(() =>
  import("./pages/IngestionValidationPage")
);

const IngestionPublishPage = lazyPage(() =>
  import("./pages/IngestionPublishPage")
);

const ReportsPage = lazyPage(() =>
  import("./pages/ReportsPage")
);

const TeamPage = lazyPage(() =>
  import("./pages/TeamPage")
);

const BillingPage = lazyPage(() =>
  import("./pages/BillingPage")
);

const SettingsPage = lazyPage(() =>
  import("./pages/SettingsPage")
);

const SupportPage = lazyPage(() =>
  import("./pages/SupportPage")
);

const ModelLabPage = lazyPage(() =>
  import("./pages/ModelLabPage")
);

const ModelGovernancePage = lazyPage(() =>
  import("./pages/ModelGovernancePage")
);

const EmployeeOpsPage = lazyPage(() =>
  import("./pages/EmployeeOpsPage")
);

const SupportHelpWidget = lazyPage(() => import("./components/support/SupportHelpWidget"));

// ─────────────────────────────────────────────────────────────
// Admin pages
// ─────────────────────────────────────────────────────────────

const AdminDashboard = lazyPage(() =>
  import("./pages/admin/AdminDashboard")
);

const AdminUsersPage = lazyPage(() =>
  import("./pages/admin/AdminUsersPage")
);

const AdminOrganizationsPage = lazyPage(() =>
  import("./pages/admin/AdminOrganizationsPage")
);

const AdminPaymentsPage = lazyPage(() =>
  import("./pages/admin/AdminPaymentsPage")
);

const AdminActivityPage = lazyPage(() =>
  import("./pages/admin/AdminActivityPage")
);

const AdminAuditPage = lazyPage(() =>
  import("./pages/admin/AdminAuditPage")
);

const AdminSystemLogsPage = lazyPage(() =>
  import("./pages/admin/AdminSystemLogsPage")
);

const AdminAIUsagePage = lazyPage(() =>
  import("./pages/admin/AdminAIUsagePage")
);

const AdminIngestionJobsPage = lazyPage(() =>
  import("./pages/admin/AdminIngestionJobsPage")
);

const AdminReportsPage = lazyPage(() =>
  import("./pages/admin/AdminReportsPage")
);

// ─────────────────────────────────────────────────────────────
// Roles
// ─────────────────────────────────────────────────────────────

const ADMIN_ROLES = ["admin", "owner"];
const BILLING_ROLES = ["admin", "owner", "billing_admin"];
const TEAM_ROLES = ["admin", "owner"];

function normalizeRole(role) {
  const raw = String(role || "").trim().toLowerCase();

  const aliases = {
    superuser: "admin",
    administrator: "admin",
    team_admin: "admin",
    manager: "admin",
    member: "analyst",
    user: "analyst",
    billing: "billing_admin",
    billingadmin: "billing_admin",
  };

  const normalized = aliases[raw] || raw;

  if (
    [
      "owner",
      "admin",
      "billing_admin",
      "reviewer",
      "analyst",
      "tester",
      "viewer",
      "client",
    ].includes(normalized)
  ) {
    return normalized;
  }

  return "viewer";
}

function getUserRole(user) {
  return normalizeRole(
    user?.role ||
      user?.access_role ||
      user?.user_role ||
      user?.account_role ||
      user?.membership_role
  );
}

function RoleGate({
  roles,
  children,
  redirectTo = "/dashboard",
}) {
  const { user, loading } = useAuth?.() || {};

  if (loading) return <PageFallback />;

  const role = getUserRole(user);

  if (!roles.includes(role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

function AdminOnly({ children }) {
  return <RoleGate roles={ADMIN_ROLES}>{children}</RoleGate>;
}

function BillingOnly({ children }) {
  return <RoleGate roles={BILLING_ROLES}>{children}</RoleGate>;
}

function TeamOnly({ children }) {
  return <RoleGate roles={TEAM_ROLES}>{children}</RoleGate>;
}

function ProtectedFeatureRoute({ feature, children }) {
  return <FeatureRoute feature={feature}>{children}</FeatureRoute>;
}

function TesterBlocked() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: 18,
        padding: 24,
        textAlign: "center",
        color: "var(--txt-mid, #64748b)",
      }}
    >
      <div style={{ fontSize: 42 }}>🔒</div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: "var(--txt-hi, #0f172a)",
        }}
      >
        Restricted Area
      </div>
      <div
        style={{
          maxWidth: 420,
          lineHeight: 1.7,
          fontSize: 14,
        }}
      >
        Your tester account does not currently have access to this section.
      </div>
    </div>
  );
}

function NotTester({ children }) {
  const { user, loading } = useAuth?.() || {};

  if (loading) return <PageFallback />;

  const role = getUserRole(user);

  if (role === "tester") {
    return <TesterBlocked />;
  }

  return children;
}

// ─────────────────────────────────────────────────────────────
// Simple system pages
// ─────────────────────────────────────────────────────────────

function EmailVerifiedPage() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-page, #f3f6fb)",
        padding: 24,
        color: "var(--txt-hi, #0f172a)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "var(--bg-card, #ffffff)",
          border: "1px solid var(--border-subtle, rgba(15, 23, 42, 0.08))",
          borderRadius: 24,
          padding: 40,
          textAlign: "center",
          color: "var(--txt-hi, #0f172a)",
          boxShadow: "var(--shadow-panel, 0 18px 50px rgba(15,23,42,0.08))",
        }}
      >
        <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          Email Verified
        </div>
        <div
          style={{
            color: "var(--txt-mid, #475569)",
            fontSize: 14,
            marginBottom: 28,
            lineHeight: 1.7,
          }}
        >
          Your Signaliq Intelligence account email has been successfully confirmed.
        </div>
        <a
          href="/dashboard"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "12px 20px",
            borderRadius: 12,
            background: "var(--blue, #2563eb)",
            color: "#ffffff",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          Open Dashboard
        </a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Route helpers
// ─────────────────────────────────────────────────────────────

function StandardRoute({ feature, children }) {
  if (!feature) return children;

  return (
    <ProtectedFeatureRoute feature={feature}>
      {children}
    </ProtectedFeatureRoute>
  );
}

function RestrictedRoute({ children, roleGate = null }) {
  const content = roleGate ? roleGate(children) : children;

  return <NotTester>{content}</NotTester>;
}


// ─────────────────────────────────────────────────────────────
// Route observability
// ─────────────────────────────────────────────────────────────

function getReadablePageName(pathname = "/") {
  const clean = String(pathname || "/").replace(/^\/+/, "").replace(/\/+$/, "");

  if (!clean) return "Dashboard";

  const first = clean.split("/")[0];

  const map = {
    dashboard: "Dashboard",
    admin: "Admin",
    companies: "Companies",
    portfolio: "Portfolio",
    reports: "Reports",
    reporting: "Reports",
    ingestion: "Ingestion",
    "data-ingestion": "Ingestion",
    forecasting: "Forecasting",
    forecast: "Forecasting",
    fhi: "Financial Health Index",
    "financial-health-index": "Financial Health Index",
    bsp: "Business Survival Probability",
    "business-strength-profile": "Business Strength Profile",
    "strength-profile": "Business Strength Profile",
    "business-survival-probability": "Business Survival Probability",
    "scenario-analysis": "Scenario Analysis",
    scenarios: "Scenario Analysis",
    sensitivity: "Sensitivity Analysis",
    decomposition: "Decomposition",
    "unit-economics": "Unit Economics",
    benchmarks: "Benchmarks",
    benchmarking: "Benchmarks",
    "ai-analyst": "AI Analyst",
    "ai-insights": "AI Analyst",
    "global-risk-monitor": "Global Risk Monitor",
    "global-risk": "Global Risk Monitor",
    "risk-monitor": "Global Risk Monitor",
    "global-risk-engine": "Global Risk Monitor",
    "global-risk-map": "Global Risk Monitor",
    support: "Support Center",
    help: "Support Center",
    "help-center": "Support Center",
  };

  return map[first] || clean.replace(/[-/]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function RouteActivityTracker() {
  const location = useLocation();
  const previousPathRef = useRef("");

  useEffect(() => {
    const pathname = location.pathname || "/";
    const search = location.search || "";
    const routeKey = `${pathname}${search}`;

    if (previousPathRef.current === routeKey) return;
    previousPathRef.current = routeKey;

    const pageName = getReadablePageName(pathname);
    const isAdmin = pathname.startsWith("/admin");

    const metadata = {
      page_name: pageName,
      pathname,
      search,
      route_key: routeKey,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      title: typeof document !== "undefined" ? document.title || null : null,
    };

    // Non-blocking analytics: never delay route rendering/navigation.
    const track = () => {
      try {
        const result = isAdmin
          ? adminApi.trackAdminPageView(pathname, metadata)
          : adminApi.trackPageView(pathname, metadata);

        if (result && typeof result.catch === "function") {
          result.catch(() => {});
        }
      } catch {
        // Activity tracking must never break page navigation.
      }
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(track, { timeout: 1500 });
    } else {
      window.setTimeout(track, 0);
    }
  }, [location.pathname, location.search]);

  return null;
}


function AuthInvalidSessionBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    const onInvalid = (event) => {
      const next = event?.detail?.next || `${window.location.pathname || "/"}${window.location.search || ""}`;
      const target = `/login?reason=session_expired${next && next !== "/login" ? `&next=${encodeURIComponent(next)}` : ""}`;
      navigate(target, { replace: true });
    };

    window.addEventListener("signaliq:auth-invalid", onInvalid);
    return () => window.removeEventListener("signaliq:auth-invalid", onInvalid);
  }, [navigate]);

  return null;
}
function RouteChangeLoader() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const routeKeyRef = useRef(`${location.pathname}${location.search}`);
  const mountedRef = useRef(false);

  useEffect(() => {
    const pathname = location.pathname || "/";
    const search = location.search || "";
    const routeKey = `${pathname}${search}`;

    const isPublicRoute =
      pathname === "/login" ||
      pathname === "/register" ||
      pathname === "/privacy" ||
      pathname === "/terms" ||
      pathname === "/email-verified";

    if (isPublicRoute) {
      setVisible(false);
      routeKeyRef.current = routeKey;
      return;
    }

    const isFirstProtectedLoad = !mountedRef.current;
    const didRouteChange = routeKeyRef.current !== routeKey;

    mountedRef.current = true;

    if (!isFirstProtectedLoad && !didRouteChange) return;

    routeKeyRef.current = routeKey;
    setVisible(true);

    let done = false;
    let observer = null;
    let minTimer = null;
    let maxTimer = null;
    let pollTimer = null;

    const MIN_VISIBLE_MS = 450;
    const MAX_VISIBLE_MS = 12000;

    const hasMeaningfulPageContent = () => {
      if (typeof document === "undefined") return true;

      const scrollRoot =
        document.querySelector(".nfi-scroll") ||
        document.querySelector(".nfi-main") ||
        document.querySelector("#root");

      if (!scrollRoot) return false;

      const loadingOnly = scrollRoot.querySelector(
        ".siq-page-loading, .siq-route-loading, .siq-module-loading"
      );

      const errorCard = scrollRoot.querySelector(
        ".error-boundary, .route-error, .page-error, [data-error-boundary='true']"
      );

      if (errorCard) return true;

      const meaningfulSelectors = [
        ".nfi-page",
        ".dashboard-page",
        ".strength-page",
        ".fhi-page",
        ".survival-page",
        ".benchmarks-page",
        ".unit-economics-page",
        ".variance-page",
        ".forecasting-page",
        ".sensitivity-page",
        ".scenario-page",
        ".decomposition-page",
        ".ai-analyst-page",
        ".reports-page",
        ".ingestion-page",
        ".portfolio-page",
        ".companies-page",
        ".settings-page",
        ".support-page",
        ".card",
        ".nfi-card",
        ".chart-card",
        ".module-card",
        ".stat-card",
        ".kpi-card",
        "[data-page-ready='true']",
      ];

      const nodes = meaningfulSelectors.flatMap((selector) =>
        Array.from(scrollRoot.querySelectorAll(selector))
      );

      const visibleNodes = nodes.filter((node) => {
        if (!node || loadingOnly?.contains(node)) return false;

        const rect = node.getBoundingClientRect();
        const text = String(node.textContent || "").trim();

        return rect.width > 80 && rect.height > 40 && text.length > 8;
      });

      return visibleNodes.length > 0;
    };

    const finish = () => {
      if (done) return;
      done = true;

      if (observer) observer.disconnect();
      if (minTimer) window.clearTimeout(minTimer);
      if (maxTimer) window.clearTimeout(maxTimer);
      if (pollTimer) window.clearInterval(pollTimer);

      setVisible(false);
    };

    const tryFinish = () => {
      if (done) return;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (hasMeaningfulPageContent()) {
            finish();
          }
        });
      });
    };

    minTimer = window.setTimeout(() => {
      tryFinish();
    }, MIN_VISIBLE_MS);

    maxTimer = window.setTimeout(() => {
      finish();
    }, MAX_VISIBLE_MS);

    pollTimer = window.setInterval(() => {
      tryFinish();
    }, 350);

    if (typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(() => {
        tryFinish();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "data-page-ready"],
      });
    }

    return () => {
      done = true;
      if (observer) observer.disconnect();
      if (minTimer) window.clearTimeout(minTimer);
      if (maxTimer) window.clearTimeout(maxTimer);
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [location.pathname, location.search]);

  if (!visible) return null;

  return (
    <PageLoading
      overlay
      compact
      label={`Loading ${getReadablePageName(location.pathname)}…`}
      sublabel="Preparing SignalIQ Intelligence workspace view."
    />
  );
}


// ─────────────────────────────────────────────────────────────
// Routes
// ─────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      <Route path="/email-verified" element={<EmailVerifiedPage />} />
      <Route path="/privacy" element={<LegalPages page="privacy" />} />
      <Route path="/terms" element={<LegalPages page="terms" />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          {/* Home / command center */}
          <Route index element={<DashboardPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/command-center" element={<DashboardPage />} />
          <Route path="/command" element={<DashboardPage />} />

          {/* Start / setup */}
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/launch-onboarding" element={<OnboardingPage />} />
          <Route path="/launch-setup" element={<OnboardingPage />} />

          {/* Company and portfolio registry */}
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/company-registry" element={<CompaniesPage />} />
          <Route path="/registry" element={<CompaniesPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/portfolio-overview" element={<PortfolioPage />} />

          {/* Core scorecards */}
          <Route
            path="/business-strength-profile"
            element={
              <StandardRoute feature="strength_profile">
                <StrengthProfilePage />
              </StandardRoute>
            }
          />
          <Route
            path="/strength-profile"
            element={
              <StandardRoute feature="strength_profile">
                <StrengthProfilePage />
              </StandardRoute>
            }
          />
          <Route
            path="/bsp-profile"
            element={
              <StandardRoute feature="strength_profile">
                <StrengthProfilePage />
              </StandardRoute>
            }
          />

          <Route
            path="/business-survival-probability"
            element={
              <StandardRoute feature="survival">
                <BSPSurvivalPage />
              </StandardRoute>
            }
          />
          <Route
            path="/bsp"
            element={
              <StandardRoute feature="survival">
                <BSPSurvivalPage />
              </StandardRoute>
            }
          />
          <Route
            path="/survival"
            element={
              <StandardRoute feature="survival">
                <BSPSurvivalPage />
              </StandardRoute>
            }
          />
          <Route
            path="/survival-probability"
            element={
              <StandardRoute feature="survival">
                <BSPSurvivalPage />
              </StandardRoute>
            }
          />

          <Route
            path="/fhi"
            element={
              <StandardRoute feature="fhi">
                <FHIProfilePage />
              </StandardRoute>
            }
          />
          <Route
            path="/financial-health-index"
            element={
              <StandardRoute feature="fhi">
                <FHIProfilePage />
              </StandardRoute>
            }
          />

          <Route
            path="/benchmarks"
            element={
              <StandardRoute feature="benchmarks">
                <BenchmarksPage />
              </StandardRoute>
            }
          />
          <Route
            path="/benchmarking"
            element={
              <StandardRoute feature="benchmarks">
                <BenchmarksPage />
              </StandardRoute>
            }
          />

          {/* Risk / warning modules */}
          <Route
            path="/covenants"
            element={
              <StandardRoute feature="covenants">
                <CovenantBreachPage />
              </StandardRoute>
            }
          />
          <Route
            path="/covenant-breach"
            element={
              <StandardRoute feature="covenants">
                <CovenantBreachPage />
              </StandardRoute>
            }
          />
          <Route
            path="/early-warnings"
            element={
              <StandardRoute feature="early_warnings">
                <EarlyWarningsPage />
              </StandardRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <StandardRoute feature="early_warnings">
                <EarlyWarningsPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ews"
            element={
              <StandardRoute feature="early_warnings">
                <EarlyWarningsPage />
              </StandardRoute>
            }
          />

          {/* Performance / modeling modules */}
          <Route
            path="/variance"
            element={
              <StandardRoute feature="variance">
                <VariancePage />
              </StandardRoute>
            }
          />
          <Route
            path="/variance-analysis"
            element={
              <StandardRoute feature="variance">
                <VariancePage />
              </StandardRoute>
            }
          />
          <Route
            path="/unit-economics"
            element={
              <StandardRoute feature="unit_economics">
                <UnitEconomicsPage />
              </StandardRoute>
            }
          />
          <Route
            path="/decomposition"
            element={
              <StandardRoute feature="decomposition">
                <DecompositionPage />
              </StandardRoute>
            }
          />
          <Route
            path="/scenarios"
            element={
              <StandardRoute feature="scenarios">
                <ScenarioAnalysisPage />
              </StandardRoute>
            }
          />
          <Route
            path="/scenario-analysis"
            element={
              <StandardRoute feature="scenarios">
                <ScenarioAnalysisPage />
              </StandardRoute>
            }
          />
          <Route
            path="/sensitivity"
            element={
              <StandardRoute feature="sensitivity">
                <SensitivityPage />
              </StandardRoute>
            }
          />
          <Route
            path="/sensitivity-analysis"
            element={
              <StandardRoute feature="sensitivity">
                <SensitivityPage />
              </StandardRoute>
            }
          />
          <Route
            path="/goal-seek"
            element={
              <StandardRoute feature="goal_seek">
                <GoalSeekPage />
              </StandardRoute>
            }
          />
          <Route
            path="/forecasting"
            element={
              <StandardRoute feature="forecasting">
                <ForecastingPage />
              </StandardRoute>
            }
          />
          <Route
            path="/forecast"
            element={
              <StandardRoute feature="forecasting">
                <ForecastingPage />
              </StandardRoute>
            }
          />
          <Route
            path="/investment-appraisal"
            element={
              <StandardRoute feature="investment_appraisal">
                <InvestmentAppraisalPage />
              </StandardRoute>
            }
          />
          <Route
            path="/financial-statement-visuals"
            element={
              <StandardRoute feature="ingestion">
                <FinancialStatementsVisualsPage />
              </StandardRoute>
            }
          />
          <Route
            path="/financial-visuals"
            element={
              <StandardRoute feature="ingestion">
                <FinancialStatementsVisualsPage />
              </StandardRoute>
            }
          />
          <Route
            path="/cost-benefit"
            element={
              <StandardRoute feature="cost_benefit">
                <CostBenefitPage />
              </StandardRoute>
            }
          />

          {/* Intelligence */}
          <Route
            path="/macro"
            element={
              <StandardRoute feature="macro">
                <MacroIntelligencePage />
              </StandardRoute>
            }
          />
          <Route
            path="/macro-intelligence"
            element={
              <StandardRoute feature="macro">
                <MacroIntelligencePage />
              </StandardRoute>
            }
          />
          <Route
            path="/daily-briefing"
            element={
              <StandardRoute feature="daily_briefing">
                <DailyBriefingPage />
              </StandardRoute>
            }
          />
          <Route path="/global-risk-monitor" element={<GlobalRiskMonitorPage />} />
          <Route path="/global-risk" element={<GlobalRiskMonitorPage />} />
          <Route path="/risk-monitor" element={<GlobalRiskMonitorPage />} />
          <Route
            path="/esg"
            element={
              <StandardRoute feature="esg">
                <ESGPage />
              </StandardRoute>
            }
          />

          <Route
            path="/ai-analyst"
            element={
              <StandardRoute feature="ai_insights">
                <AIAnalystPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ai-insights"
            element={
              <StandardRoute feature="ai_insights">
                <AIAnalystPage />
              </StandardRoute>
            }
          />

          {/* Ingestion */}
          <Route
            path="/ingestion"
            element={
              <StandardRoute feature="ingestion">
                <IngestionPage />
              </StandardRoute>
            }
          />
          <Route
            path="/data-ingestion"
            element={
              <StandardRoute feature="ingestion">
                <IngestionPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ingestion/jobs"
            element={
              <StandardRoute feature="ingestion">
                <IngestionJobsPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ingestion-jobs"
            element={
              <StandardRoute feature="ingestion">
                <IngestionJobsPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ingestion/jobs/:jobId/review"
            element={
              <StandardRoute feature="ingestion">
                <IngestionReviewPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ingestion/jobs/:jobId/validation"
            element={
              <StandardRoute feature="ingestion">
                <IngestionValidationPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ingestion/jobs/:jobId/publish"
            element={
              <StandardRoute feature="ingestion">
                <IngestionPublishPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ingestion/review/:jobId"
            element={
              <StandardRoute feature="ingestion">
                <IngestionReviewPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ingestion/validation/:jobId"
            element={
              <StandardRoute feature="ingestion">
                <IngestionValidationPage />
              </StandardRoute>
            }
          />
          <Route
            path="/ingestion/publish/:jobId"
            element={
              <StandardRoute feature="ingestion">
                <IngestionPublishPage />
              </StandardRoute>
            }
          />

          {/* Reports */}
          <Route
            path="/reports"
            element={
              <StandardRoute feature="reports">
                <ReportsPage />
              </StandardRoute>
            }
          />
          <Route
            path="/reporting"
            element={
              <StandardRoute feature="reports">
                <ReportsPage />
              </StandardRoute>
            }
          />

          {/* User / org settings */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/help" element={<SupportPage />} />
          <Route path="/help-center" element={<SupportPage />} />


          <Route
            path="/billing"
            element={
              <RestrictedRoute roleGate={(children) => <BillingOnly>{children}</BillingOnly>}>
                <BillingPage />
              </RestrictedRoute>
            }
          />

          <Route
            path="/team"
            element={
              <RestrictedRoute roleGate={(children) => <TeamOnly>{children}</TeamOnly>}>
                <TeamPage />
              </RestrictedRoute>
            }
          />

          {/* Restricted model / workforce areas */}
          <Route
            path="/model-lab"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <ModelLabPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/model-governance"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <ModelGovernancePage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/ai-workforce"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <EmployeeOpsPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/employee-ops"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <EmployeeOpsPage />
              </RestrictedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminDashboard />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminUsersPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminOrganizationsPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminPaymentsPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/activity"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminActivityPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/activity-logs"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminActivityPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminAuditPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminAuditPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/system-logs"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminSystemLogsPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/ai-usage"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminAIUsagePage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/ingestion"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminIngestionJobsPage />
              </RestrictedRoute>
            }
          />

          <Route
            path="/admin/ingestion-jobs"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminIngestionJobsPage />
              </RestrictedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <RestrictedRoute roleGate={(children) => <AdminOnly>{children}</AdminOnly>}>
                <AdminReportsPage />
              </RestrictedRoute>
            }
          />

          {/* Legacy redirects */}
          <Route path="/business-strength" element={<Navigate to="/business-strength-profile" replace />} />
          <Route path="/strength" element={<Navigate to="/business-strength-profile" replace />} />
          <Route path="/health" element={<Navigate to="/fhi" replace />} />
          <Route path="/risk" element={<Navigate to="/early-warnings" replace />} />
          <Route path="/global-risk-engine" element={<Navigate to="/global-risk-monitor" replace />} />
          <Route path="/global-risk-map" element={<Navigate to="/global-risk-monitor" replace />} />

          {/* Final fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

// ─────────────────────────────────────────────────────────────
// Root App
// ─────────────────────────────────────────────────────────────

export default function App() {
  return (
    <AppProviders>
      <BrowserRouter>
        <AuthInvalidSessionBridge />
        <RouteActivityTracker />
        <RouteChangeLoader />
        <SupportHelpWidget />
        <AppRoutes />
      </BrowserRouter>
    </AppProviders>
  );
}
